import React from "react";
import { Card, Button, Divider, InputNumber, Input, Select, Upload, Typography, message } from "antd";
import { DeleteOutlined, SettingOutlined, UploadOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface LabelElement {
  id: string;
  type: "text" | "barcode" | "qrcode" | "logo" | "line";
  x: number;
  y: number;
  w: number;
  h: number;
  fontSize?: number;
  fontWeight?: string;
  text?: string;
  align?: "left" | "center" | "right";
  barcodeFormat?: "code128" | "qr";
  rotation?: number;
}

interface PropertiesSidebarProps {
  selectedElement: LabelElement | undefined;
  updateElementProp: (id: string, prop: keyof LabelElement, val: any) => void;
  deleteElement: (id: string) => void;
  logoBase64: string;
  setLogoBase64: (val: string) => void;
}

export const PropertiesSidebar: React.FC<PropertiesSidebarProps> = ({
  selectedElement,
  updateElementProp,
  deleteElement,
  logoBase64,
  setLogoBase64
}) => {
  return (
    <Card title="Properties Config" size="small" className="shadow-sm border border-gray-200 rounded-lg bg-white">
      {selectedElement ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Text strong className="capitalize text-xs text-indigo-700">{selectedElement.type} Element</Text>
            <Button 
              type="text" 
              danger 
              size="small" 
              icon={<DeleteOutlined className="text-xs" />} 
              onClick={() => deleteElement(selectedElement.id)}
            >
              Delete
            </Button>
          </div>
          
          <Divider className="my-2" />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-gray-500">X Position (px)</label>
              <InputNumber 
                value={selectedElement.x}
                onChange={(val) => updateElementProp(selectedElement.id, "x", val || 0)}
                className="w-full text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500">Y Position (px)</label>
              <InputNumber 
                value={selectedElement.y}
                onChange={(val) => updateElementProp(selectedElement.id, "y", val || 0)}
                className="w-full text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-gray-500">Width (px)</label>
              <InputNumber 
                value={selectedElement.w}
                onChange={(val) => updateElementProp(selectedElement.id, "w", val || 0)}
                className="w-full text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500">Height (px)</label>
              <InputNumber 
                value={selectedElement.h}
                onChange={(val) => updateElementProp(selectedElement.id, "h", val || 0)}
                className="w-full text-xs"
              />
            </div>
          </div>

          {selectedElement.type === "text" && (
            <>
              <div>
                <label className="text-[10px] font-semibold text-gray-500 block mb-1">Text Template / Value</label>
                <Input 
                  value={selectedElement.text}
                  onChange={(e) => updateElementProp(selectedElement.id, "text", e.target.value)}
                  className="text-xs"
                />
                <div className="mt-1.5 flex flex-wrap gap-1">
                  <span className="text-[9px] bg-gray-100 hover:bg-indigo-50 border border-gray-200 text-gray-600 hover:text-indigo-600 cursor-pointer px-1 py-0.5 rounded transition" onClick={() => updateElementProp(selectedElement.id, "text", (selectedElement.text || "") + " {product_name}")}>{`{name}`}</span>
                  <span className="text-[9px] bg-gray-100 hover:bg-indigo-50 border border-gray-200 text-gray-600 hover:text-indigo-600 cursor-pointer px-1 py-0.5 rounded transition" onClick={() => updateElementProp(selectedElement.id, "text", (selectedElement.text || "") + " Rs. {price}")}>{`{price}`}</span>
                  <span className="text-[9px] bg-gray-100 hover:bg-indigo-50 border border-gray-200 text-gray-600 hover:text-indigo-600 cursor-pointer px-1 py-0.5 rounded transition" onClick={() => updateElementProp(selectedElement.id, "text", (selectedElement.text || "") + " {barcode}")}>{`{barcode}`}</span>
                  <span className="text-[9px] bg-gray-100 hover:bg-indigo-50 border border-gray-200 text-gray-600 hover:text-indigo-600 cursor-pointer px-1 py-0.5 rounded transition" onClick={() => updateElementProp(selectedElement.id, "text", (selectedElement.text || "") + " {expiry_date}")}>{`{expiry}`}</span>
                  <span className="text-[9px] bg-gray-100 hover:bg-indigo-50 border border-gray-200 text-gray-600 hover:text-indigo-600 cursor-pointer px-1 py-0.5 rounded transition" onClick={() => updateElementProp(selectedElement.id, "text", (selectedElement.text || "") + " {cost_code}")}>{`{cost_code}`}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-gray-500">Font Size (px)</label>
                  <InputNumber 
                    value={selectedElement.fontSize}
                    onChange={(val) => updateElementProp(selectedElement.id, "fontSize", val || 10)}
                    className="w-full text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500">Weight</label>
                  <Select
                    value={selectedElement.fontWeight}
                    onChange={(val) => updateElementProp(selectedElement.id, "fontWeight", val)}
                    className="w-full text-xs"
                  >
                    <Select.Option value="normal">Normal</Select.Option>
                    <Select.Option value="semibold">Medium</Select.Option>
                    <Select.Option value="bold">Bold</Select.Option>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-gray-500">Alignment</label>
                <Select
                  value={selectedElement.align}
                  onChange={(val) => updateElementProp(selectedElement.id, "align", val)}
                  className="w-full text-xs"
                >
                  <Select.Option value="left">Left</Select.Option>
                  <Select.Option value="center">Center</Select.Option>
                  <Select.Option value="right">Right</Select.Option>
                </Select>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-gray-500">Rotation</label>
                <Select
                  value={selectedElement.rotation || 0}
                  onChange={(val) => updateElementProp(selectedElement.id, "rotation", val)}
                  className="w-full text-xs"
                >
                  <Select.Option value={0}>0°</Select.Option>
                  <Select.Option value={90}>90°</Select.Option>
                  <Select.Option value={180}>180°</Select.Option>
                  <Select.Option value={270}>270°</Select.Option>
                </Select>
              </div>
            </>
          )}

          {selectedElement.type === "barcode" && (
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">Barcode Tag Value</label>
              <Input 
                value={selectedElement.text}
                onChange={(e) => updateElementProp(selectedElement.id, "text", e.target.value)}
                className="text-xs"
              />
            </div>
          )}

          {selectedElement.type === "qrcode" && (
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">QR Code Tag Value</label>
              <Input 
                value={selectedElement.text}
                onChange={(e) => updateElementProp(selectedElement.id, "text", e.target.value)}
                className="text-xs"
              />
            </div>
          )}

          {selectedElement.type === "logo" && (
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">Upload Label Logo</label>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    if (e.target?.result) {
                      setLogoBase64(e.target.result as string);
                      message.success("Logo uploaded successfully.");
                    }
                  };
                  reader.readAsDataURL(file);
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />} className="w-full text-xs">Upload Image</Button>
              </Upload>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-10 text-gray-400 text-xs">
          <SettingOutlined className="text-xl mb-2 block text-gray-300" />
          Select any item on the canvas to configure properties
        </div>
      )}
    </Card>
  );
};
